import React from 'react';
import { ExternalLink, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { crmService } from '@/lib/api';
import { StateBadge, fmtMoney, fmtDateTime } from './crmHelpers';

const CrmHeader = ({ data, onChanged, tenantSlug }) => {
  const { debtor, settings } = data;
  const states = settings.account_states.filter((s) => s.is_active || s.id === debtor.state?.id);

  const changeState = async (stateId) => {
    try {
      await crmService.setState(debtor.id, { state_id: stateId });
      toast.success('Estado actualizado');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cambiar el estado');
    }
  };

  return (
    <div className="border-b border-slate-200 pb-4" data-testid="crm-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-900 truncate" data-testid="crm-debtor-name">{debtor.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-1.5">
            {[
              debtor.dni && `DNI ${debtor.dni}`,
              debtor.cuit && `CUIT ${debtor.cuit}`,
              debtor.portfolio_name,
              debtor.plate && <span key="plate" className="font-mono">{debtor.plate}</span>,
              debtor.actas_count > 0 && <span key="actas" className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />{debtor.actas_count} acta{debtor.actas_count > 1 ? 's' : ''}</span>,
            ].filter(Boolean).map((part, i) => <React.Fragment key={i}>{i > 0 && <span>·</span>}<span>{part}</span></React.Fragment>)}
          </p>
          {(debtor.address || debtor.city) && <p className="text-xs text-slate-500">{[debtor.address, debtor.city, debtor.province].filter(Boolean).join(', ')}</p>}
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-wide text-slate-400">Deuda</p>
          <p className="text-xl font-bold text-slate-900" data-testid="crm-debt">{fmtMoney(debtor.total_debt)}</p>
          {debtor.discount_amount != null && debtor.discount_amount < debtor.total_debt && <p className="text-xs text-emerald-700">c/quita {fmtMoney(debtor.discount_amount)}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Estado</span>
          <Select value={debtor.state?.id || ''} onValueChange={changeState}>
            <SelectTrigger className="h-8 w-56 text-xs" data-testid="crm-state-select"><SelectValue placeholder="Sin estado" /></SelectTrigger>
            <SelectContent>
              {states.map((s) => <SelectItem key={s.id} value={s.id}><StateBadge state={s} /></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {debtor.last_interaction_at && <span className="text-xs text-slate-500">Última gestión: {fmtDateTime(debtor.last_interaction_at)} · {debtor.last_interaction_summary}</span>}
        {tenantSlug && debtor.direct_code && (
          <a href={`/t/${tenantSlug}/${debtor.direct_code}`} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-teal-700 hover:underline inline-flex items-center gap-1" data-testid="crm-portal-link">
            Portal del cliente <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
};

export default CrmHeader;
